import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import MainSite from './components/MainSite';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('adminToken');
  if (!token) return <Navigate to="/login" replace />;
  return children;
};

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [activeGallery, setActiveGallery] = useState([]);
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [selectedStay, setSelectedStay] = useState(null);
  const [toast, setToast] = useState({ message: '', isVisible: false });

  // Real Data State
  const [stays, setStays] = useState([]);
  const [wonders, setWonders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchPublicData();
  }, []);

  const fetchPublicData = async () => {
    // Try to load from cache first for immediate display
    const cachedStays = localStorage.getItem('staysData');
    const cachedWonders = localStorage.getItem('wondersData');
    
    if (cachedStays && cachedWonders) {
      try {
        setStays(JSON.parse(cachedStays));
        setWonders(JSON.parse(cachedWonders));
        setIsLoading(false); // Stop skeleton loaders if we have cached data
      } catch (e) {
        console.error('Error parsing cached data', e);
      }
    } else {
      setIsLoading(true);
    }

    try {
      const [stayRes, wonderRes] = await Promise.all([
        fetch(`https://jaladhara.onrender.com/api/stay`),
        fetch(`https://jaladhara.onrender.com/api/discover`)
      ]);
      const [stayData, wonderData] = await Promise.all([stayRes.json(), wonderRes.json()]);
      
      setStays(stayData);
      setWonders(wonderData);
      
      // Update cache
      localStorage.setItem('staysData', JSON.stringify(stayData));
      localStorage.setItem('wondersData', JSON.stringify(wonderData));
    } catch (error) {
      console.error('Error fetching public data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrolled(currentScrollY > 50);
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setHeaderVisible(false);
      } else {
        setHeaderVisible(true);
      }
      setLastScrollY(currentScrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const scrollTo = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const openLightbox = (index, gallery) => {
    setActiveGallery(gallery.map(url => ({ url, title: 'Gallery Image', desc: '' })));
    setLightboxIndex(index);
  };

  const closeLightbox = () => setLightboxIndex(null);
  const openBooking = (stay) => { setSelectedStay(stay); setIsBookingOpen(true); };
  const closeBooking = () => setIsBookingOpen(false);
  const showToast = (message) => setToast({ message, isVisible: true });
  const closeToast = () => setToast({ ...toast, isVisible: false });
  
  const nextImage = (e) => {
    if (e) e.stopPropagation();
    setLightboxIndex((prev) => (prev + 1) % activeGallery.length);
  };

  const prevImage = (e) => {
    if (e) e.stopPropagation();
    setLightboxIndex((prev) => (prev - 1 + activeGallery.length) % activeGallery.length);
  };

  return (
    <Router>
      <Routes>
        <Route path="/" element={
          <MainSite 
            stays={stays}
            wonders={wonders}
            isLoading={isLoading}
            scrolled={scrolled}
            headerVisible={headerVisible}
            setIsMenuOpen={setIsMenuOpen}
            scrollTo={scrollTo}
            isMenuOpen={isMenuOpen}
            openLightbox={openLightbox}
            openBooking={openBooking}
            showToast={showToast}
            lightboxIndex={lightboxIndex}
            activeGallery={activeGallery}
            closeLightbox={closeLightbox}
            nextImage={nextImage}
            prevImage={prevImage}
            isBookingOpen={isBookingOpen}
            closeBooking={closeBooking}
            selectedStay={selectedStay}
            toast={toast}
            closeToast={closeToast}
          />
        } />
        <Route path="/login" element={<Login />} />
        <Route path="/admin" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
      </Routes>
    </Router>
  );
}

export default App;
